import React, { useEffect, useState } from 'react';
import { workflowAPI } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Workflow, Plus, Trash2, Edit2, Loader2, ArrowDown } from 'lucide-react';

const ENTITY_TYPES = ['leave', 'wfh', 'expense', 'comp_off'];
const APPROVER_TYPES = [
  { v: 'manager', l: 'Reporting Manager' },
  { v: 'hr', l: 'HR Admin' },
  { v: 'super_admin', l: 'Super Admin' },
  { v: 'role', l: 'Custom Role' },
  { v: 'user', l: 'Specific User' },
];

const emptyStage = () => ({ name: '', approver_type: 'manager', approver_value: '', sla_hours: 24, optional: false });
const emptyForm = () => ({ name: '', entity_type: 'leave', company_id: '', is_default: false, is_active: true, stages: [emptyStage()] });

const WorkflowTemplates = () => {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false, edit: null });
  const [form, setForm] = useState(emptyForm());
  const [busy, setBusy] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = filter !== 'all' ? { entity_type: filter } : {};
      const r = await workflowAPI.listTemplates(params);
      setRows(r.data || []);
    } catch (e) { toast.error('Failed'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [filter]);

  const openCreate = () => { setForm(emptyForm()); setDialog({ open: true, edit: null }); };
  const openEdit = (t) => { setForm({ name: t.name, entity_type: t.entity_type, company_id: t.company_id || '', is_default: !!t.is_default, is_active: t.is_active !== false, stages: t.stages || [emptyStage()] }); setDialog({ open: true, edit: t }); };

  const addStage = () => setForm({ ...form, stages: [...form.stages, emptyStage()] });
  const removeStage = (idx) => setForm({ ...form, stages: form.stages.filter((_, i) => i !== idx) });
  const setStage = (idx, patch) => setForm({ ...form, stages: form.stages.map((s, i) => i === idx ? { ...s, ...patch } : s) });

  const save = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    if (form.stages.length === 0) { toast.error('At least one stage'); return; }
    for (const s of form.stages) {
      if (!s.name) { toast.error('All stages need a name'); return; }
      if ((s.approver_type === 'role' || s.approver_type === 'user') && !s.approver_value) {
        toast.error(`Stage "${s.name}" needs an approver value`); return;
      }
    }
    setBusy(true);
    try {
      const payload = {
        name: form.name, entity_type: form.entity_type,
        company_id: form.company_id || null,
        is_default: form.is_default, is_active: form.is_active,
        stages: form.stages.map((s) => ({ ...s, sla_hours: Number(s.sla_hours || 24), approver_value: s.approver_value || null })),
      };
      if (dialog.edit) await workflowAPI.updateTemplate(dialog.edit.id, payload);
      else await workflowAPI.createTemplate(payload);
      toast.success('Saved');
      setDialog({ open: false, edit: null });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete "${t.name}"?`)) return;
    try { await workflowAPI.deleteTemplate(t.id); toast.success('Deleted'); fetchData(); }
    catch { toast.error('Delete failed'); }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Workflow size={22} />Approval Workflows</h1>
          <p className="text-muted-foreground text-sm">Build multi-stage approval chains for leave, WFH, expenses, and more.</p>
        </div>
        <div className="flex gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40" data-testid="wf-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {ENTITY_TYPES.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={openCreate} data-testid="wf-create-btn"><Plus size={14} className="mr-1" />New Workflow</Button>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 size={14} className="animate-spin" />Loading…</div>
      ) : rows.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">No workflow templates. Create one to get started.</CardContent></Card>
      ) : (
        <div className="space-y-2" data-testid="wf-list">
          {rows.map((t) => (
            <Card key={t.id} data-testid={`wf-row-${t.id}`}>
              <CardContent className="p-4 flex items-center gap-3 flex-wrap">
                <Workflow size={16} className="text-muted-foreground" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{t.name}</span>
                    <Badge variant="outline">{t.entity_type}</Badge>
                    {t.is_default && <Badge variant="success">Default</Badge>}
                    {!t.is_active && <Badge variant="secondary">Inactive</Badge>}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {(t.stages || []).map((s, i) => (
                      <span key={i}>{s.name} <span className="opacity-60">({s.approver_type})</span>{i < t.stages.length - 1 ? ' → ' : ''}</span>
                    ))}
                  </div>
                </div>
                <Button size="sm" variant="ghost" onClick={() => openEdit(t)} data-testid={`wf-edit-${t.id}`}><Edit2 size={14} /></Button>
                <Button size="sm" variant="ghost" onClick={() => remove(t)} data-testid={`wf-delete-${t.id}`}><Trash2 size={14} className="text-red-600" /></Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialog.open} onOpenChange={(o) => setDialog({ open: o, edit: dialog.edit })}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="wf-dialog">
          <DialogHeader>
            <DialogTitle>{dialog.edit ? 'Edit workflow' : 'New workflow'}</DialogTitle>
            <DialogDescription>Define the stages an entity (leave, WFH, expense…) must clear to be approved.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="wf-name" /></div>
              <div><Label>Entity type</Label>
                <Select value={form.entity_type} onValueChange={(v) => setForm({ ...form, entity_type: v })}>
                  <SelectTrigger data-testid="wf-entity"><SelectValue /></SelectTrigger>
                  <SelectContent>{ENTITY_TYPES.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2 border rounded p-2"><Switch checked={form.is_default} onCheckedChange={(v) => setForm({ ...form, is_default: v })} data-testid="wf-default" /><Label className="text-sm">Default for entity type</Label></div>
              <div className="flex items-center gap-2 border rounded p-2"><Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="wf-active" /><Label className="text-sm">Active</Label></div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Stages</Label>
                <Button size="sm" variant="outline" onClick={addStage} data-testid="wf-add-stage"><Plus size={12} className="mr-1" />Add stage</Button>
              </div>
              <div className="space-y-2">
                {form.stages.map((s, idx) => (
                  <Card key={idx} data-testid={`wf-stage-${idx}`}>
                    <CardContent className="p-3 space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground w-6">#{idx + 1}</span>
                        <Input value={s.name} onChange={(e) => setStage(idx, { name: e.target.value })} placeholder="Stage name" className="flex-1" data-testid={`wf-stage-name-${idx}`} />
                        <Button size="icon" variant="ghost" onClick={() => removeStage(idx)} disabled={form.stages.length === 1} data-testid={`wf-stage-remove-${idx}`}><Trash2 size={14} className="text-red-600" /></Button>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <Label className="text-xs">Approver</Label>
                          <Select value={s.approver_type} onValueChange={(v) => setStage(idx, { approver_type: v })}>
                            <SelectTrigger data-testid={`wf-stage-approver-${idx}`}><SelectValue /></SelectTrigger>
                            <SelectContent>{APPROVER_TYPES.map((a) => <SelectItem key={a.v} value={a.v}>{a.l}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        {(s.approver_type === 'role' || s.approver_type === 'user') && (
                          <div>
                            <Label className="text-xs">{s.approver_type === 'role' ? 'Role name' : 'User ID'}</Label>
                            <Input value={s.approver_value || ''} onChange={(e) => setStage(idx, { approver_value: e.target.value })} data-testid={`wf-stage-value-${idx}`} />
                          </div>
                        )}
                        <div>
                          <Label className="text-xs">SLA (hours)</Label>
                          <Input type="number" min="1" value={s.sla_hours} onChange={(e) => setStage(idx, { sla_hours: e.target.value })} data-testid={`wf-stage-sla-${idx}`} />
                        </div>
                        <div className="flex items-center gap-2 col-span-3"><Switch checked={s.optional} onCheckedChange={(v) => setStage(idx, { optional: v })} data-testid={`wf-stage-optional-${idx}`} /><Label className="text-xs">Optional (auto-skip if no approver resolved)</Label></div>
                      </div>
                    </CardContent>
                    {idx < form.stages.length - 1 && (
                      <div className="flex justify-center -mt-1 -mb-2"><ArrowDown size={16} className="text-muted-foreground" /></div>
                    )}
                  </Card>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog({ open: false, edit: null })}>Cancel</Button>
            <Button onClick={save} disabled={busy} data-testid="wf-save">{busy ? <Loader2 size={14} className="animate-spin mr-2" /> : null}Save workflow</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WorkflowTemplates;
